// =============================================================================
// DRAFT REVIEW MODAL COMPONENT
// End-of-day workflow for reviewing and approving draft time entries
// =============================================================================

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import {
  CheckIcon,
  TrashIcon,
  XMarkIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

interface DraftReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: string;
}

interface DraftEntry {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  break_duration: number;
  notes: string | null;
  hourly_rate: number | null;
  projects: {
    name: string;
    hourly_rate: number | null;
  } | null;
  activities: {
    name: string;
  } | null;
  employees: {
    full_name: string;
  } | null;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const DraftReviewModal: React.FC<DraftReviewModalProps> = ({
  isOpen,
  onClose,
  employeeId,
}) => {
  const queryClient = useQueryClient();
  
  // Local state for selections
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'all'>('today');

  // =============================================================================
  // CALCULATED DATE RANGES
  // =============================================================================
  
  const getDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (dateFilter) {
      case 'today':
        return {
          start: today.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0],
        };
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return {
          start: weekAgo.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0],
        };
      case 'all':
        return {
          start: '2000-01-01',
          end: '2099-12-31',
        };
      default:
        return {
          start: today.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0],
        };
    }
  };

  const dateRange = getDateRange();

  // =============================================================================
  // DATA FETCHING
  // =============================================================================

  const { data: draftEntries, isLoading } = useQuery({
    queryKey: ['draftEntries', employeeId, dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('time_entries')
        .select(`
          *,
          projects(name, hourly_rate),
          employees(full_name),
          activities(name)
        `)
        .eq('employee_id', employeeId)
        .eq('status', 'draft')
        .gte('date', dateRange.start)
        .lte('date', dateRange.end)
        .order('date', { ascending: false })
        .order('start_time', { ascending: false });

      if (error) throw error;
      return data as DraftEntry[];
    },
    enabled: isOpen && !!employeeId,
  });

  // =============================================================================
  // CALCULATIONS
  // =============================================================================

  const calculateDuration = (start: string, end: string, breakMinutes: number = 0) => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    let totalMinutes = endMinutes - startMinutes;
    if (totalMinutes < 0) totalMinutes += 24 * 60; // Handle overnight
    
    return totalMinutes - breakMinutes;
  };

  const totals = React.useMemo(() => {
    if (!draftEntries) return { hours: 0, minutes: 0, earnings: 0, count: 0 };
    
    let totalMinutes = 0;
    let totalEarnings = 0;
    
    draftEntries.forEach(entry => {
      const netMinutes = calculateDuration(
        entry.start_time,
        entry.end_time,
        entry.break_duration || 0
      );
      totalMinutes += netMinutes;
      
      const rate = entry.hourly_rate || entry.projects?.hourly_rate || 0;
      totalEarnings += (netMinutes / 60) * rate;
    });
    
    return {
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60,
      earnings: totalEarnings,
      count: draftEntries.length,
    };
  }, [draftEntries]);

  // =============================================================================
  // SELECTION HANDLERS
  // =============================================================================

  const handleSelectAll = () => {
    if (!draftEntries) return;
    
    if (selectedIds.length === draftEntries.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(draftEntries.map(e => e.id));
    }
  };

  const handleToggleEntry = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // =============================================================================
  // MUTATIONS
  // =============================================================================

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: async (entryIds: string[]) => {
      const { error } = await supabase
        .from('time_entries')
        .update({ status: 'approved' })
        .in('id', entryIds);
      
      if (error) throw error;
    },
    onSuccess: (_, entryIds) => {
      queryClient.invalidateQueries({ queryKey: ['draftEntries'] });
      queryClient.invalidateQueries({ queryKey: ['companyData'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      setSelectedIds([]);
      toast.success(`${entryIds.length} ${entryIds.length === 1 ? 'entry' : 'entries'} goedgekeurd!`);
    },
    onError: (error: any) => {
      toast.error(`Fout bij goedkeuren: ${error.message}`);
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (entryIds: string[]) => {
      const { error } = await supabase
        .from('time_entries')
        .delete()
        .in('id', entryIds);
      
      if (error) throw error;
    },
    onSuccess: (_, entryIds) => {
      queryClient.invalidateQueries({ queryKey: ['draftEntries'] });
      queryClient.invalidateQueries({ queryKey: ['companyData'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      setSelectedIds([]);
      toast.success(`${entryIds.length} ${entryIds.length === 1 ? 'entry' : 'entries'} verwijderd!`);
    },
    onError: (error: any) => {
      toast.error(`Fout bij verwijderen: ${error.message}`);
    },
  });

  // Individual delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await supabase
        .from('time_entries')
        .delete()
        .eq('id', entryId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draftEntries'] });
      queryClient.invalidateQueries({ queryKey: ['companyData'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      toast.success('Entry verwijderd!');
    },
    onError: (error: any) => {
      toast.error(`Fout bij verwijderen: ${error.message}`);
    },
  });

  // =============================================================================
  // HANDLERS
  // =============================================================================

  const handleBulkApprove = () => {
    if (selectedIds.length === 0) {
      toast.error('Selecteer eerst entries om goed te keuren');
      return;
    }

    const confirmation = window.confirm(
      `Weet je zeker dat je ${selectedIds.length} ${selectedIds.length === 1 ? 'entry' : 'entries'} wilt goedkeuren?`
    );

    if (confirmation) {
      bulkApproveMutation.mutate(selectedIds);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.error('Selecteer eerst entries om te verwijderen');
      return;
    }

    const confirmation = window.confirm(
      `⚠️ WAARSCHUWING: Je staat op het punt ${selectedIds.length} ${selectedIds.length === 1 ? 'entry' : 'entries'} permanent te verwijderen.\n\nDeze actie kan niet ongedaan gemaakt worden!\n\nWeet je het zeker?`
    );

    if (confirmation) {
      bulkDeleteMutation.mutate(selectedIds);
    }
  };

  const handleEdit = (entry: DraftEntry) => {
    // TODO: Implement in Step 5 - Open TimeEntryForm in edit mode
    toast('Edit functionaliteit komt in Step 5', { icon: '⚠️' });
    console.log('Edit entry:', entry);
  };

  const handleDelete = (id: string) => {
    const confirmation = window.confirm(
      '⚠️ WAARSCHUWING: Deze entry wordt permanent verwijderd.\n\nDeze actie kan niet ongedaan gemaakt worden!\n\nWeet je het zeker?'
    );

    if (confirmation) {
      deleteMutation.mutate(id);
    }
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            📋 Draft Review
            {draftEntries && draftEntries.length > 0 && (
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                ({draftEntries.length} {draftEntries.length === 1 ? 'entry' : 'entries'})
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 space-y-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Periode
              </label>
              <select
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value as 'today' | 'week' | 'all');
                  setSelectedIds([]);
                }}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="today">Vandaag</option>
                <option value="week">Deze week</option>
                <option value="all">Alle drafts</option>
              </select>
            </div>
          </div>

          {/* Bulk Actions */}
          {draftEntries && draftEntries.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {selectedIds.length === draftEntries.length ? '☐ Deselecteer alles' : '☑ Selecteer alles'}
              </button>
              
              {selectedIds.length > 0 && (
                <>
                  <button
                    onClick={handleBulkApprove}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center gap-2"
                  >
                    <CheckIcon className="h-4 w-4" />
                    Goedkeuren ({selectedIds.length})
                  </button>
                  
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 flex items-center gap-2"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Verwijderen ({selectedIds.length})
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Drafts laden...</p>
            </div>
          ) : !draftEntries || draftEntries.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Geen draft entries
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {dateFilter === 'today' && 'Je hebt vandaag geen drafts om te reviewen.'}
                {dateFilter === 'week' && 'Je hebt deze week geen drafts om te reviewen.'}
                {dateFilter === 'all' && 'Je hebt geen drafts om te reviewen.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {draftEntries.map((entry) => {
                const netMinutes = calculateDuration(
                  entry.start_time,
                  entry.end_time,
                  entry.break_duration || 0
                );
                const hours = Math.floor(netMinutes / 60);
                const minutes = netMinutes % 60;
                const rate = entry.hourly_rate || entry.projects?.hourly_rate || 0;
                const earnings = (netMinutes / 60) * rate;

                return (
                  <div
                    key={entry.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(entry.id)}
                        onChange={() => handleToggleEntry(entry.id)}
                        className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />

                      {/* Entry Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {entry.projects?.name || 'Geen project'}
                              </span>
                              <span className="text-gray-500 dark:text-gray-400">•</span>
                              <span className="text-gray-700 dark:text-gray-300">
                                {entry.start_time} - {entry.end_time}
                              </span>
                              <span className="text-gray-500 dark:text-gray-400">•</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}
                              </span>
                              {rate > 0 && (
                                <>
                                  <span className="text-gray-500 dark:text-gray-400">•</span>
                                  <span className="font-medium text-green-600 dark:text-green-400">
                                    €{earnings.toFixed(2)}
                                  </span>
                                </>
                              )}
                            </div>
                            
                            <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                              {entry.activities?.name || 'Geen activiteit'}
                              {entry.notes && ` • "${entry.notes}"`}
                            </div>
                            
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {new Date(entry.date).toLocaleDateString('nl-NL', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(entry)}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                              title="Bewerken"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                              title="Verwijderen"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with Totals */}
        {draftEntries && draftEntries.length > 0 && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-900 dark:text-white">
                  {totals.count} {totals.count === 1 ? 'entry' : 'entries'}
                </span>
              </div>
              
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Totaal: </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {String(totals.hours).padStart(2, '0')}:{String(totals.minutes).padStart(2, '0')}
                  </span>
                </div>
                
                {totals.earnings > 0 && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Verdiensten: </span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      €{totals.earnings.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftReviewModal;